import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { FileService } from '../../../core/service/file.service';
import { Contract } from '../../model/contract';
import { LeaseAgreementDocumentService } from '../../service/data/lease-agreement-document.service';
import { Subscription } from 'rxjs';
import { LeaseAgreementDocument } from '../../model/lease-agreement-document';
import { SelectionItem } from '../../../core/component/select/select.component';
import {
  ESignatory,
  InformationChannelKind,
  SignatureReminderSettingKind,
  SignatureTypeKind,
  SigningRoleKind,
} from '../../model/esignatory';
import { LocalDate } from '../../../core/model/local-date';
import { MessageService } from '../../../core/service/message.service';
import { UserAccountService } from '../../../common/service/user-account.service';
import { configureESignatoryContactValidation } from '../../helper/esignatory-form-validation';

@Component({
    selector: 'app-lease-agreement-document-form',
    templateUrl: './lease-agreement-document-form.component.html',
    styleUrls: ['./lease-agreement-document-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LeaseAgreementDocumentFormComponent implements OnDestroy, OnInit {
  constructor(
    private _actionService: ActionWindowService,
    private _dataService: LeaseAgreementDocumentService,
    private _fb: FormBuilder,
    private _messageService: MessageService,
    private _fcf: FormControlExFactory,
    private _fileService: FileService,
    private _userAccount: UserAccountService
  ) {
    this.expirationDate = LocalDate.fromDate(LocalDate.today().addDays(7));
    this.esignSettingSelected = true;
  }

  private _subscription: Subscription = new Subscription();
  dataContext: Contract;
  form: FormGroup;
  esignSettingSelected: boolean;

  cancel() {
    this._actionService.close();
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
    this._subscription = null;
  }

  ngOnInit() {
    this.form = this._fb.group({
      caption: this._fcf.createWithOptions('LEASE_AGREEMENT_DOCUMENT.CAPTION', '', { required: true, length: 255 }),
      document: this._fcf.createMandatory('', null),
      sendForEsign: this._fcf.create('ESIGNATURE.SEND_FOR_ESIGN', false),
      errandDescription: this._fcf.createMandatory('ESIGNATURE.DESCRIPTION', ''),
      // The default expiration date is computed in the constructor but used to be dropped on the
      // floor: an empty FormGroup here left expirationDate without year/month/day, so the model
      // setter turned it into undefined and JSON.stringify omitted the field entirely. The API then
      // saw no expiration date at all and every e-sign upload failed. A seeded mandatory control -
      // the same shape the sibling e-sign forms use (esign-key-reciept-document,
      // esign-lease-agreement-document) - always sends a date and blocks an emptied one.
      expirationDate: this._fcf.createMandatory('ESIGNATURE.EXPIRATION_DATE', this.expirationDate),
      leaseAgreementId: this._fcf.createHidden(this.dataContext.id),
      signatories: this._fb.array([]),
    });

    if (this.dataContext.tenantOneName !== '') {
      let signatory = new ESignatory();
      signatory.civicNumber = this.dataContext.tenantOneContactIdentification;
      signatory.name = this.dataContext.tenantOneName;
      signatory.email = this.dataContext.tenantOneEmail;
      signatory.phone = this.dataContext.tenantOnePhone;
      signatory.signingRole = SigningRoleKind.Tenant;
      this.addSignatory(signatory);
    }

    if (this.dataContext.tenantTwoName !== '') {
      let signatory = new ESignatory();
      signatory.civicNumber = this.dataContext.tenantTwoContactIdentification;
      signatory.name = this.dataContext.tenantTwoName;
      signatory.email = this.dataContext.tenantTwoEmail;
      signatory.phone = this.dataContext.tenantTwoPhone;
      signatory.signingRole = SigningRoleKind.Tenant;
      this.addSignatory(signatory);
    }

    this._subscription.add(
      this._userAccount.current.subscribe(x => {
        let signatory = new ESignatory();
        signatory.civicNumber = x.civicNumber;
        signatory.name = x.name;
        signatory.email = x.email;
        signatory.phone = x.phoneNumber;
        signatory.signingRole = SigningRoleKind.CompanyRepresentative;
        this.addSignatory(signatory);
      })
    );
  }

  expirationDate: LocalDate;

  civicNumberMask = /^(\d{8}\-\d{0,4})|(\d{1,8})/;
  readonly civicNumberMaskFormat: string = 'ÅÅÅÅMMDD-NNNN';

  informationChannels = [
    new SelectionItem('ESIGNATURE.INFORMATION_CHANNEL.EMAIL', InformationChannelKind.Email),
    new SelectionItem('ESIGNATURE.INFORMATION_CHANNEL.SMS', InformationChannelKind.Sms),
  ];

  signingRoles = [
    new SelectionItem('ESIGNATURE.SIGNING_ROLE.NOT_SPECIFIED', SigningRoleKind.NotSpecified),
    new SelectionItem('ESIGNATURE.SIGNING_ROLE.COMPANY_REPRESENTATIVE', SigningRoleKind.CompanyRepresentative),
    new SelectionItem('ESIGNATURE.SIGNING_ROLE.TENANT', SigningRoleKind.Tenant),
  ];

  signatureTypeSelection = [
    new SelectionItem('ESIGNATURE.SIGNATURE_TYPE.BANKID', SignatureTypeKind.BankId),
    new SelectionItem('ESIGNATURE.SIGNATURE_TYPE.CLICK', SignatureTypeKind.Click),
  ];

  signatureReminderSettingSelection = [
    new SelectionItem('ESIGNATURE.SIGNATURE_REMINDER_SETTING.NO_REMINDERS', SignatureReminderSettingKind.NoReminders),
    new SelectionItem('ESIGNATURE.SIGNATURE_REMINDER_SETTING.ONCE_A_DAY', SignatureReminderSettingKind.OnceADay),
  ];

  private addSignatory(signatory: ESignatory) {
    const signatories = this.form.controls['signatories'] as FormArray;
    const signatoryForm = this._fb.group({
        id: this._fcf.createHidden(signatory.id),
        civicNumber: this._fcf.create('ESIGNATURE.SIGNATORY.CIVIC_NUMBER', signatory.civicNumber),
        email: this._fcf.createEmail('ESIGNATURE.SIGNATORY.EMAIL', signatory.email),
        informationChannel: this._fcf.createMandatory('ESIGNATURE.SIGNATORY.INFORMATION_CHANNEL', signatory.informationChannel),
        signatureType: this._fcf.createMandatory('ESIGNATURE.SIGNATORY.SIGNATURE_TYPE', signatory.signatureType),
        name: this._fcf.createMandatory('ESIGNATURE.SIGNATORY.NAME', signatory.name),
        phone: this._fcf.create('ESIGNATURE.SIGNATORY.PHONE', signatory.phone),
        signOrder: this._fcf.create('ESIGNATURE.SIGNATORY.SIGN_ORDER', signatory.signOrder),
        signingRole: this._fcf.createMandatory('COMMON.ROLE', signatory.signingRole),
        signatureReminderSetting: this._fcf.createMandatory(
          'ESIGNATURE.SIGNATORY.SIGNATURE_REMINDER_SETTING',
          signatory.signatureReminderSetting
        ),
      });
    signatories.push(signatoryForm);
    this._subscription.add(configureESignatoryContactValidation(signatoryForm));
  }

  get signatoryForms(): FormGroup[] {
    return (this.form.controls['signatories'] as FormArray).controls.map(c => c as FormGroup);
  }

  newSignatory() {
    this.addSignatory(new ESignatory());
  }

  deleteSignatory(index: number) {
    const signatories = this.form.controls['signatories'] as FormArray;
    signatories.removeAt(index);
  }

  onFileChange(files: FileList) {
    if (!files || files.length === 0) {
      this.form.controls['document'].reset();
      this.form.controls['caption'].reset();
      return;
    }
    const file = files.item(0);
    const error = this._fileService.validateDocument(file);
    if (error === null) {
      this.form.controls['document'].setValue(file);
      const dotIndex = file.name.lastIndexOf('.');
      const fileName = dotIndex > 0 ? file.name.substring(0, dotIndex) : file.name;
      this.form.controls['errandDescription'].setValue('Signering av ' + fileName);
      this.form.controls['caption'].setValue(fileName);
    } else {
      this.form.controls['document'].reset();
      this.form.controls['caption'].reset();
    }
  }

  get esignActive(): boolean {
    if (this.form.controls['sendForEsign'].value && this.esignSettingSelected) {
      return true;
    }
    return false;
  }

  onSubmit() {
    // Surface all field-level feedback before any branch returns from a save attempt.
    this.form.markAllAsTouched();

    if (this.esignActive) {
      if (this.form.controls['errandDescription'].value === '') {
        this._messageService.showError('ERROR.ESIGNMESSAGE_NOT_EMPTY');
        return;
      }

      if (!this.form.valid) return;
    }

    if (this.form.controls['document'].value === null) {
      this._messageService.showError('ERROR.FILE_NOT_EMPTY');
      return;
    }

    if (this.form.controls['caption'].value === '') {
      this._messageService.showError('ERROR.CAPTION_NOT_EMPTY');
      return;
    }

    if (!this.form.controls['caption'].valid) return;

    this._subscription.add(
      this._dataService
        .add(Object.assign(new LeaseAgreementDocument(), this.form.value))
        .subscribe(() => this._actionService.complete(true)) // Completing with true to trigger refresh of documents list in parent component
    );
  }
}
